'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Entering,
  FloatingFooter,
  LoadingLogo,
  NoResults,
  Pagination,
  Spinning,
  SummaryCard,
  Typography
} from 'components';
import { DateType } from 'components/Inputs/DateRange/Datepicker';
import {
  COMMENTS_DEFAULT,
  DATE_PREFERRED_DEFAULT,
  DATE_RANGE_DEFAULT,
  EDITOR_ID_DEFAULT,
  LANGUAGES_DEFAULT,
  PAGE_CANT_RESET_DEFAULT,
  PAGE_DEFAULT,
  QUALITY_DEFAULT,
  QUERY_DEFAULT,
  QUOTE_DEFAULT,
  SHOW_DUPLICATES_DEFAULT,
  STATUS_DEFAULT,
  TRANSLATED_BY_AGENT_DEFAULT,
  WORKFLOW_ID_DEFAULT
} from 'constants/defaults';
import { FILTERS_TRANSLATION_REQUESTS_PERSISTED_STATE } from 'constants/persistedStates';
import { THEME_TRANSLATION_REQUEST_STATUS } from 'constants/status';
import { getComments } from 'factories/getComments';
import { useInView } from 'framer-motion';
import {
  CommentsFilterEnum,
  QualityEnum,
  QueryTranslationRequestsArgs,
  TranslationRequest,
  TranslationRequestStatusEnum,
  TranslationRequestStatusFilterEnum
} from 'gql/graphql';
import usePersistentContext from 'hooks/usePersistenceState';
import { CloseIcon } from 'lib/svgs/Icons';
import _ from 'lodash';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useFeatureFlags } from 'providers/ClientProviders/useFeatureFlags';
import { useSummary } from 'providers/ClientProviders/useSummary';
import { useTranslationRequests } from 'providers/TranslationRequests/useTranslationRequests';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { TScheme } from 'types/TScheme';
import { useDebounce } from 'use-debounce';
import { delay } from 'utils/delay';
import { extractParam } from 'utils/paramsUtil';
import { endDate } from 'utils/time/endDate';
import * as yup from 'yup';
import EditQuoteCard from './_components/EditQuoteCard';
import FiltersTranslation from './_components/FiltersTranslation';
import RequestCard from './_components/RequestCard';
import { useModalTranslationRequestContext } from './_context/ModalTranslationRequestContext';

export interface FiltersTranslationForm
  extends Omit<
    QueryTranslationRequestsArgs,
    | 'editorId'
    | 'page'
    | 'perPage'
    | 'sortOrder'
    | 'sourceLanguageCode'
    | 'targetLanguageCode'
    | 'createdAfter'
    | 'createdBefore'
  > {
  languages: { sourceLanguageCode: string; targetLanguageCode: string };
  dateRange: {
    startDate: DateType;
    endDate: DateType;
  };
  datePreferred: {
    startDate: DateType;
    endDate: DateType;
  };
  status: TranslationRequestStatusFilterEnum[];
  editorId: string | undefined;
  translatedByAgent: boolean;
}

export interface VersionsSelect {
  trVersions: string[];
}

export default function TranslationRequestsPage() {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { data: featureFlags } = useFeatureFlags();

  const [quoteEditId, setQuoteEditId] = useState<string | null>(null);

  const [, setQueryParamsFiltersUrl] = usePersistentContext(
    FILTERS_TRANSLATION_REQUESTS_PERSISTED_STATE
  );

  const isInternalUpdate = useRef(false);

  const [quoteId, setQuoteId] = useState<string | null>(null);

  const [page, setPage] = useState(PAGE_DEFAULT);
  const [isSelectItem, setSelectItem] = useState(false);

  const pageInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT) as number,
    [searchParams]
  );

  const pageCantResetInitial = useMemo(
    () =>
      extractParam(searchParams, KeysEnum.PageCantReset, PAGE_CANT_RESET_DEFAULT).toString() ===
      'true',
    [searchParams]
  );

  const queryInitial = searchParams.get(KeysEnum.Query) ?? QUERY_DEFAULT;

  const quoteInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Quote, QUOTE_DEFAULT),
    [searchParams]
  );

  const languagesInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Languages, LANGUAGES_DEFAULT),
    [searchParams]
  );

  const dateRangeInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.DateRange, DATE_RANGE_DEFAULT),
    [searchParams]
  );

  const datePreferredInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.DatePreferred, DATE_PREFERRED_DEFAULT),
    [searchParams]
  );

  const qualityInitial = useMemo(
    () => (searchParams.get(KeysEnum.Quality) ?? QUALITY_DEFAULT) as QualityEnum,
    [searchParams]
  );

  const editorIdInitial = useMemo(
    () => (searchParams.get(KeysEnum.EditorId) ?? EDITOR_ID_DEFAULT) as string | undefined,
    [searchParams]
  );

  const showDuplicatesInitial = SHOW_DUPLICATES_DEFAULT;

  const translatedByAgentInitial = extractParam(
    searchParams,
    KeysEnum.TranslatedByAgent,
    TRANSLATED_BY_AGENT_DEFAULT
  );

  const commentsInitial = useMemo(
    () => (searchParams.get(KeysEnum.Comments) ?? COMMENTS_DEFAULT) as CommentsFilterEnum,
    [searchParams]
  );

  const workflowIdInitial = useMemo(
    () => (searchParams.get(KeysEnum.WorkflowId) ?? WORKFLOW_ID_DEFAULT) as string | undefined,
    [searchParams]
  );

  const statusInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Status, STATUS_DEFAULT),
    [searchParams]
  );

  const { open, trVersions, openModal, setVersion, setTrVersions } =
    useModalTranslationRequestContext();

  const schema = yup.object().shape({} as TScheme<FiltersTranslationForm>);
  const {
    control,
    reset,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersTranslationForm>({
    resolver: yupResolver(schema)
  });

  const query = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.Query,
    defaultValue: QUERY_DEFAULT
  });

  const [deferredQuery] = useDebounce(query, 1000);

  const languages = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.Languages,
    defaultValue: LANGUAGES_DEFAULT
  });

  const dateRange = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.DateRange,
    defaultValue: DATE_RANGE_DEFAULT
  });

  const datePreferred = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.DatePreferred,
    defaultValue: DATE_PREFERRED_DEFAULT
  });

  const quality = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.Quality,
    defaultValue: QUALITY_DEFAULT
  });

  const editorId = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.EditorId,
    defaultValue: EDITOR_ID_DEFAULT
  });

  const showDuplicates = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.ShowDuplicates,
    defaultValue: showDuplicatesInitial
  });

  const translatedByAgent = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.TranslatedByAgent,
    defaultValue: translatedByAgentInitial
  });

  const comments = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.Comments,
    defaultValue: COMMENTS_DEFAULT
  });

  const workflowId = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.WorkflowId,
    defaultValue: WORKFLOW_ID_DEFAULT
  });

  const status = useWatch<FiltersTranslationForm>({
    control,
    name: KeysEnum.Status,
    defaultValue: STATUS_DEFAULT
  }) as TranslationRequestStatusFilterEnum[];

  const isQuoted = status.includes(TranslationRequestStatusFilterEnum.Unordered);

  const isReadyToReview = status.includes(TranslationRequestStatusFilterEnum.ReadyToReview);

  const isFinished = status.includes(TranslationRequestStatusFilterEnum.Finished);

  const filtersAdded = useMemo(
    () =>
      [
        page !== PAGE_DEFAULT,
        query !== QUERY_DEFAULT,
        languages?.sourceLanguageCode !== LANGUAGES_DEFAULT.sourceLanguageCode,
        languages?.targetLanguageCode !== LANGUAGES_DEFAULT.targetLanguageCode,
        dateRange?.startDate !== DATE_RANGE_DEFAULT.startDate,
        dateRange?.endDate !== DATE_RANGE_DEFAULT.startDate,
        datePreferred?.startDate !== datePreferred.startDate,
        datePreferred?.endDate !== datePreferred.startDate,
        quality !== QUALITY_DEFAULT,
        showDuplicates === SHOW_DUPLICATES_DEFAULT,
        translatedByAgent !== TRANSLATED_BY_AGENT_DEFAULT,
        !!editorId,
        !!workflowId,
        comments !== COMMENTS_DEFAULT,
        !!status?.length
      ].some(Boolean),
    [
      page,
      query,
      languages?.sourceLanguageCode,
      languages?.targetLanguageCode,
      dateRange?.startDate,
      dateRange?.endDate,
      datePreferred.startDate,
      datePreferred?.endDate,
      quality,
      showDuplicates,
      translatedByAgent,
      editorId,
      workflowId,
      comments,
      status?.length
    ]
  );

  const queryVariables: QueryTranslationRequestsArgs = {
    query: deferredQuery,
    showDuplicates,
    translatedByAgent,
    sourceLanguageCode: languages?.sourceLanguageCode,
    targetLanguageCode: languages?.targetLanguageCode,
    createdAfter: dateRange?.startDate ? new Date(dateRange?.startDate).toISOString() : null,
    createdBefore: dateRange?.endDate ? endDate(dateRange?.endDate) : null,
    proposedClientDeadlineAfter: datePreferred?.startDate
      ? new Date(datePreferred?.startDate).toISOString()
      : null,
    proposedClientDeadlineBefore: datePreferred?.endDate ? endDate(datePreferred?.endDate) : null,
    quality,
    editorId,
    comments,
    workflowId,
    status
  };

  const {
    data: translationRequests,
    isLoading,
    isFetching,
    isFetched,
    refetch
  } = useTranslationRequests(
    {
      page,
      perPage: 20,
      ...queryVariables
    },
    {
      refetchOnWindowFocus: true
    }
  );

  const { data: summary, isLoading: isLoadingSummary } = useSummary(
    {
      ...queryVariables
    },
    {
      refetchOnWindowFocus: true
    }
  );

  const disableSelect = !(
    (isQuoted || isReadyToReview || isFinished) &&
    status?.length === 1 &&
    !(isFetching && !trVersions?.length)
  );

  const totalPages = translationRequests?.pagesCount ?? 0;
  const nodesCount = translationRequests?.nodesCount ?? 0;

  const translationRequestsVersions = translationRequests?.nodes?.map((tr) => tr.version);

  const showLoading = (isLoading || isLoadingSummary) && page === 1;

  const isAllSelected = useMemo(() => {
    if (!trVersions?.length) return false;

    return translationRequestsVersions?.every((tdVersion) => trVersions?.includes(tdVersion));
  }, [trVersions, translationRequestsVersions]);

  const textButtonFooter = {
    [TranslationRequestStatusFilterEnum.Unordered]: 'order',
    [TranslationRequestStatusFilterEnum.ReadyToReview]: 'accept_translations',
    [TranslationRequestStatusFilterEnum.Finished]: 'download_translations'
  }[
    status?.at(0) as
      | TranslationRequestStatusFilterEnum.Unordered
      | TranslationRequestStatusFilterEnum.ReadyToReview
      | TranslationRequestStatusFilterEnum.Finished
  ];

  const containerRef = useRef(null);
  const isOpen = useInView(containerRef, {
    once: true
  });

  const resetFilters = useCallback(() => {
    reset();
    setPage(pageInitial);
    setValue(KeysEnum.Query, queryInitial);
    setValue(KeysEnum.Languages, languagesInitial);
    setValue(KeysEnum.DateRange, dateRangeInitial);
    setValue(KeysEnum.DatePreferred, datePreferredInitial);
    setValue(KeysEnum.Quality, qualityInitial);
    setValue(KeysEnum.EditorId, editorIdInitial);
    setValue(KeysEnum.ShowDuplicates, showDuplicatesInitial);
    setValue(KeysEnum.TranslatedByAgent, translatedByAgentInitial);
    setValue(KeysEnum.Comments, commentsInitial);
    setValue(KeysEnum.WorkflowId, workflowIdInitial);
    setValue(KeysEnum.Status, statusInitial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reset,
    pageInitial,
    setValue,
    queryInitial,
    languagesInitial,
    dateRangeInitial,
    datePreferredInitial,
    qualityInitial,
    editorIdInitial,
    showDuplicatesInitial,
    commentsInitial,
    statusInitial,
    workflowIdInitial
  ]);

  const handleSelectItem = () => setSelectItem((oldState) => !oldState);

  const handleOnEdit = (version: string) => () => setQuoteEditId(version);
  const handleCancelEdit = () => setQuoteEditId(null);

  const handleOnClick =
    (translationRequest: TranslationRequest, isQuote: boolean, commentsNew: number) => () => {
      if (isQuote) {
        openModal('quote');
        setVersion(translationRequest.version);
        return resetFilters();
      }

      if (commentsNew > 0) {
        if (typeof window !== 'undefined')
          return (window.location.href =
            `/translation/tasks-in-progress?translationRequestVersion=${translationRequest.version}` as any);

        return router.push(
          `/translation/tasks-in-progress?translationRequestVersion=${translationRequest.version}`
        );
      }

      return router.push(`/translation/show-translation/${translationRequest.version}` as any);
    };

  const handleClearSelecion = useCallback(() => {
    setValue('status', []);
    setTrVersions([]);
  }, [setValue, setTrVersions]);

  const handleToggleSelectAll = useCallback(() => {
    if (!isAllSelected)
      return setTrVersions([...(trVersions ?? []), ...(translationRequestsVersions ?? [])]);

    return setTrVersions(
      trVersions?.filter((version) => !translationRequestsVersions?.includes(version)) ?? []
    );
  }, [isAllSelected, setTrVersions, trVersions, translationRequestsVersions]);

  const handleActionFooter = useCallback(() => {
    if (status.length === 1 && trVersions?.length) {
      const firstStatus = status?.at(0);
      if (
        firstStatus === TranslationRequestStatusFilterEnum.Unordered ||
        firstStatus === TranslationRequestStatusFilterEnum.ReadyToReview ||
        firstStatus === TranslationRequestStatusFilterEnum.Finished
      ) {
        setTrVersions(trVersions.map(String));

        if (firstStatus === TranslationRequestStatusFilterEnum.Unordered) return openModal('order');

        if (firstStatus === TranslationRequestStatusFilterEnum.ReadyToReview)
          return openModal('accept');

        if (firstStatus === TranslationRequestStatusFilterEnum.Finished) return openModal('files');
      }
    }
  }, [openModal, setTrVersions, status, trVersions]);

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.Query);
      url.searchParams.delete(KeysEnum.Languages);
      url.searchParams.delete(KeysEnum.DateRange);
      url.searchParams.delete(KeysEnum.DatePreferred);
      url.searchParams.delete(KeysEnum.Quality);
      url.searchParams.delete(KeysEnum.EditorId);
      url.searchParams.delete(KeysEnum.ShowDuplicates);
      url.searchParams.delete(KeysEnum.Comments);
      url.searchParams.delete(KeysEnum.Status);
      url.searchParams.delete(KeysEnum.Page);
      url.searchParams.delete(KeysEnum.TranslatedByAgent);
      url.searchParams.delete(KeysEnum.WorkflowId);

      resetFilters();

      window.history.replaceState({}, '', url.toString());
    }
  }, [resetFilters]);

  useEffect(() => {
    setTrVersions([]);
    setSelectItem(false);
  }, [setTrVersions, status]);

  useEffect(() => {
    if (isFetched) setTrVersions([]);

    setSelectItem(false);
  }, [isFetched, setTrVersions, status]);

  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  useEffect(() => {
    isInternalUpdate.current = true;
    const debounceMs = pageCantResetInitial ? 2000 : 0;

    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);

        if (deferredQuery) url.searchParams.set(KeysEnum.Query, deferredQuery);
        else url.searchParams.delete(KeysEnum.Query);

        if (
          languages?.sourceLanguageCode !== undefined &&
          languages?.targetLanguageCode !== undefined
        )
          url.searchParams.set(KeysEnum.Languages, JSON.stringify(languages));
        else url.searchParams.delete(KeysEnum.Languages);

        if (
          dateRange?.startDate !== dateRangeInitial?.startDate &&
          dateRange?.endDate !== dateRangeInitial?.endDate
        )
          url.searchParams.set(KeysEnum.DateRange, JSON.stringify(dateRange));
        else url.searchParams.delete(KeysEnum.DateRange);

        if (
          datePreferred?.startDate !== datePreferredInitial?.startDate &&
          datePreferred?.endDate !== datePreferredInitial?.endDate
        )
          url.searchParams.set(KeysEnum.DatePreferred, JSON.stringify(datePreferred));
        else url.searchParams.delete(KeysEnum.DatePreferred);

        if (quality) url.searchParams.set(KeysEnum.Quality, quality);
        else url.searchParams.delete(KeysEnum.Quality);

        if (showDuplicates) url.searchParams.set(KeysEnum.ShowDuplicates, showDuplicates);
        else url.searchParams.delete(KeysEnum.ShowDuplicates);

        if (translatedByAgent) url.searchParams.set(KeysEnum.TranslatedByAgent, translatedByAgent);
        else url.searchParams.delete(KeysEnum.TranslatedByAgent);

        if (editorId) url.searchParams.set(KeysEnum.EditorId, editorId);
        else url.searchParams.delete(KeysEnum.EditorId);

        if (comments) url.searchParams.set(KeysEnum.Comments, comments);
        else url.searchParams.delete(KeysEnum.Comments);

        if (workflowId) url.searchParams.set(KeysEnum.WorkflowId, workflowId);
        else url.searchParams.delete(KeysEnum.WorkflowId);

        if (status?.length) url.searchParams.set(KeysEnum.Status, JSON.stringify(status));
        else url.searchParams.delete(KeysEnum.Status);

        url.searchParams.delete(KeysEnum.PageCantReset);
        url.searchParams.delete(KeysEnum.Page);

        setPage(pageInitial);
        if (pageCantResetInitial) setPage(pageInitial);
        else
          setPage(
            [
              queryInitial !== deferredQuery,
              !_.isEqual(languagesInitial, languages),
              !_.isEqual(dateRangeInitial, dateRange),
              !_.isEqual(datePreferredInitial, datePreferred),
              qualityInitial !== quality,
              showDuplicatesInitial !== showDuplicates,
              translatedByAgentInitial !== translatedByAgent,
              editorIdInitial !== editorId,
              commentsInitial !== comments,
              statusInitial !== status,
              translatedByAgent !== translatedByAgentInitial,
              workflowId !== workflowIdInitial
            ].some(Boolean) && isDirty
              ? PAGE_DEFAULT
              : pageInitial
          );

        setQueryParamsFiltersUrl(url.toString());
        window.history.replaceState({}, '', url.toString());
      }

      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 100);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [
    comments,
    editorId,
    datePreferred,
    datePreferred?.endDate,
    datePreferred?.startDate,
    datePreferredInitial?.endDate,
    datePreferredInitial?.startDate,
    dateRange,
    dateRange?.endDate,
    dateRange?.startDate,
    dateRangeInitial?.endDate,
    dateRangeInitial?.startDate,
    deferredQuery,
    languages,
    languagesInitial.sourceLanguageCode,
    languagesInitial.targetLanguageCode,
    pageInitial,
    pathname,
    quality,
    query,
    showDuplicates,
    status,
    dateRangeInitial,
    datePreferredInitial,
    queryInitial,
    languagesInitial,
    qualityInitial,
    showDuplicatesInitial,
    editorIdInitial,
    commentsInitial,
    statusInitial,
    translatedByAgent,
    translatedByAgentInitial,
    workflowId,
    workflowIdInitial,
    pageCantResetInitial,
    setQueryParamsFiltersUrl,
    isDirty
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      setQueryParamsFiltersUrl(url.toString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname, setQueryParamsFiltersUrl]);

  useEffect(() => {
    refetch();
  }, [open, refetch]);

  const handleQuoted = useCallback(async () => {
    if (quoteId) {
      await delay(300);
      openModal('quote');
      setVersion(quoteId);
      setQuoteId(null);
      window.history.pushState(
        {
          as: '/translation/translation-requests',
          url: '/translation/translation-requests',
          options: { shallow: false }
        },
        '',
        '/translation/translation-requests'
      );
    }
  }, [quoteId, openModal, setVersion]);

  useEffect(() => {
    setQuoteId(quoteInitial);
  }, [quoteInitial]);

  useEffect(() => {
    handleQuoted();
  }, [handleQuoted]);

  return (
    <>
      <div className="mt-5 flex flex-row md:gap-4 lg:gap-8">
        <div className="w-1/4">
          <Entering offset={20} delay={0.6} duration={0.2} position="top">
            <FiltersTranslation
              control={control}
              filtersAdded={filtersAdded}
              onClear={handleClear}
            />
          </Entering>
        </div>
        <div className="w-3/4 gap-10">
          <div className="mb-4">
            <Entering offset={5} delay={0.4} duration={0.2} className="flex" position="top">
              <Typography variant="medium1" className={'text-tertiary-100 '}>
                {t('translation.summary')}
              </Typography>
            </Entering>
          </div>
          <div className="mb-8 grid grid-cols-3 gap-4">
            <Entering offset={5} delay={0.6} duration={0.2} position="top">
              <SummaryCard
                description={t('translation.index.ordered')}
                value={summary?.summary?.ordered?.sentences}
                segment={summary?.summary?.ordered?.words}
              />
            </Entering>
            <Entering offset={5} delay={0.8} duration={0.2} position="top">
              <SummaryCard
                description={t('translation.index.translated')}
                value={summary?.summary?.translated?.sentences}
                segment={summary?.summary?.translated?.words}
              />
            </Entering>
            <Entering offset={5} delay={1.0} duration={0.2} position="top">
              <SummaryCard
                description={t('translation.index.savings')}
                value={`${summary?.summary?.savings?.percentage ?? 0}%`}
                segment={summary?.summary?.savings?.words}
              />
            </Entering>
          </div>
          <Entering offset={5} delay={1.2} duration={0.2} position="bottom">
            <div className="my-4 flex justify-between">
              <span className="relative flex">
                <Typography variant="medium1" className="text-tertiary-100 ">
                  {t('translation.tabs.translation_requests')} ({nodesCount})
                </Typography>
                {isFetching && !isLoading && <Spinning className="ml-4" />}
              </span>
              {!isSelectItem && (
                <a
                  data-tooltip-offset={10}
                  data-tooltip-id="tolq-white-top-end"
                  data-tooltip-html={t.raw('translation.select_item_tootip')}
                >
                  <Button
                    size="medium"
                    label={t('translation.select_item')}
                    onClick={handleSelectItem}
                    disabled={disableSelect}
                  />
                </a>
              )}
            </div>

            {showLoading && (
              <NoResults>
                <LoadingLogo size="small" show={showLoading} />
              </NoResults>
            )}
          </Entering>
          <Entering offset={5} delay={1.4} duration={0.2} position="top">
            {!showLoading && !!translationRequests?.nodes?.length ? (
              <>
                <div className="relative flex flex-col gap-4">
                  {translationRequests?.nodes?.map((translationRequest, index) => {
                    const isQuote =
                      translationRequest.status === TranslationRequestStatusEnum.Unordered;

                    const { comments, commentsNew } = getComments(translationRequest?.comments);

                    const taskId = translationRequest.comments?.find((comment) => !comment?.read)
                      ?.task?.id;

                    const languagePair = `${t(
                      `languages.${translationRequest.sourceLanguageCode}` as any
                    )} ${t('translation.to')} ${t(
                      `languages.${translationRequest.targetLanguageCode}` as any
                    )}`;

                    return (
                      <div
                        key={translationRequest.id}
                        style={{ zIndex: translationRequests?.nodes.length - index }}
                      >
                        {quoteEditId === translationRequest.version ? (
                          <EditQuoteCard
                            version={translationRequest.version}
                            status={t(`sources.${translationRequest.status}` as any)}
                            themeStatus={
                              THEME_TRANSLATION_REQUEST_STATUS[translationRequest.status]
                            }
                            isPending={
                              translationRequest.status === TranslationRequestStatusEnum.Pending
                            }
                            defaultParams={{
                              title: translationRequest.title || '',
                              purchaseOrderCode: translationRequest.purchaseOrderCode,
                              proposedClientDeadline: translationRequest.proposedClientDeadline,
                              quality: translationRequest.quality,
                              sourceLanguageCode: translationRequest.sourceLanguageCode,
                              targetLanguageCodes: [translationRequest.targetLanguageCode],
                              styleGuideReferenceId: translationRequest.styleGuideReferenceId
                            }}
                            onCancel={handleCancelEdit}
                            refetch={refetch}
                            className="mt-4"
                          />
                        ) : (
                          <RequestCard
                            title={translationRequest.title || ''}
                            code={translationRequest.version}
                            integration={translationRequest.integration}
                            version={translationRequest.version}
                            taskId={taskId}
                            date={translationRequest.createdAt}
                            duplicateOf={translationRequest.duplicateOf as string}
                            uploaded={!!translationRequest.uploaded}
                            pully={!!translationRequest.pully}
                            hasAgent={
                              !!translationRequest?.aiTranslationWorkflow?.id &&
                              featureFlags?.agentsEnabled
                            }
                            status={translationRequest.status}
                            poCode={
                              translationRequest?.purchaseOrder?.orderCode ??
                              (translationRequest?.purchaseOrderCode as string)
                            }
                            languagePair={languagePair}
                            typeTranslation={t(
                              `ordering_form.qualities.${translationRequest.quality}` as any
                            )}
                            totalWords={translationRequest.totalWordCount ?? 0}
                            ordeableOrders={
                              translationRequest?.orderDetails?.sentences?.totalOrderableWords ?? 0
                            }
                            isDuplicated={!!translationRequest?.orderDetails?.duplicateOfVersion}
                            taskNumber={translationRequest.tasks?.length}
                            totalPrice={
                              translationRequest?.orderDetails?.pricingData?.price ?? '0.00'
                            }
                            savings={translationRequest?.orderDetails?.sentences?.savings ?? '0.0%'}
                            className="mt-4"
                            isSelectItem={isSelectItem}
                            isQuote={isQuote}
                            duplicationSavings={
                              translationRequest.orderDetails?.sentences?.savings ?? '0.0%'
                            }
                            orderAcceptedDate={translationRequest?.order?.acceptedAt}
                            preferredDeliveryDate={translationRequest?.proposedClientDeadline}
                            comments={comments}
                            commentsNew={commentsNew}
                            onEdit={handleOnEdit(translationRequest.version)}
                            onClick={handleOnClick(translationRequest, isQuote, commentsNew)}
                            refetch={refetch}
                            relatedTasks={translationRequest.pendingTasks}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {!!totalPages && !!page && (
                  <div className="flex justify-center py-10">
                    <Pagination totalPages={totalPages} page={page} changePage={setPage} />
                  </div>
                )}
              </>
            ) : (
              <>{!showLoading && <NoResults>{t('components.no_results_found')}</NoResults>}</>
            )}
          </Entering>
        </div>
      </div>
      {!open && (
        <FloatingFooter disabled={disableSelect || !isSelectItem}>
          <div className="flex justify-between">
            <Button
              variant="outlined"
              size="medium"
              label={isAllSelected ? t('translation.deselect_all') : t('translation.select_all')}
              onClick={handleToggleSelectAll}
            />
            <div className="flex items-center justify-end gap-6">
              <Typography variant="regular4" className="flex gap-4 text-tertiary-100 ">
                {trVersions?.length ?? 0} {t('translation.items_selected')}
              </Typography>
              <Button
                label={
                  textButtonFooter
                    ? t(`translation.${textButtonFooter}` as any)
                    : t('translation.unselected')
                }
                className="h-auto w-16 px-9"
                disabled={!trVersions?.length}
                onClick={handleActionFooter}
              />
              <Button
                size="small"
                variant="icon-btn"
                color="neutral"
                className="h-24 w-24 !shadow-none"
                onClick={handleClearSelecion}
                startAdornment={<CloseIcon />}
              />
            </div>
          </div>
        </FloatingFooter>
      )}
    </>
  );
}
